import React, { useEffect, useState, useCallback } from "react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger } from "@/components/ui/dialog";
import { Megaphone, Plus, Trash2, Pencil, Crown, Video, Truck, ExternalLink, ImageIcon, BadgeCheck, Link2, FileText, Phone, MapPin, FileSpreadsheet } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { StatusBadge } from "@/components/dashboard-parts";
import { useAuth } from "@/context/AuthContext";

const ROLE_LABEL = { beauty_creator: "Beauty Creator", affiliator: "Affiliator", both: "Semua" };
const PLATFORM_LABEL = { tiktok: "TikTok", shopee: "Shopee", both: "TikTok + Shopee" };

const STATUS_TABS = [
  { id: "all", label: "Semua" },
  { id: "joined", label: "Sudah Join" },
  { id: "process", label: "Bukti Masuk" },
  { id: "approved", label: "Selesai" },
];
const matchStatus = (p, s) =>
  s === "all" ||
  (s === "joined" && p.status === "joined") ||
  (s === "process" && ["submitted", "manager_approved"].includes(p.status)) ||
  (s === "approved" && p.status === "approved");

export default function CampaignAdminPanel({ onChange }) {
  const { user } = useAuth();
  const isSecretariat = (user?.email || "").toLowerCase() === "secretariatshowty@gmail.com";
  const [campaigns, setCampaigns] = useState([]);
  const [roleFilter, setRoleFilter] = useState("beauty_creator");
  const [parts, setParts] = useState([]);

  const loadCampaigns = useCallback(() => api.get("/campaigns").then((r) => { setCampaigns(r.data); onChange?.(); }).catch(() => {}), [onChange]);
  const exportExcel = async () => {
    try {
      const res = await api.get("/campaigns/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "showtynion-campaigns.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("Gagal export Excel"); }
  };
  const loadParts = useCallback((role) => api.get(`/campaign-participations?role_filter=${role}`).then((r) => setParts(r.data)).catch(() => {}), []);
  const [statusFilter, setStatusFilter] = useState("all");
  const [campFilter, setCampFilter] = useState("all");
  const [preview, setPreview] = useState(null);
  useEffect(() => { loadCampaigns(); }, [loadCampaigns]);
  useEffect(() => { loadParts(roleFilter); }, [roleFilter, loadParts]);

  const counts = {
    total: parts.length,
    joined: parts.filter((p) => p.status === "joined").length,
    process: parts.filter((p) => ["submitted", "manager_approved"].includes(p.status)).length,
    approved: parts.filter((p) => p.status === "approved").length,
    withProof: parts.filter((p) => p.proof_photo).length,
  };
  const filtered = parts.filter((p) => matchStatus(p, statusFilter) && (campFilter === "all" || p.campaign_id === campFilter));

  const review = async (id, status) => {
    try {
      await api.patch(`/campaign-participations/${id}/status`, { status });
      toast.success(status === "rejected" ? "Ditolak" : status === "approved" ? "Final approved (CEO)" : "Disetujui manager — menunggu CEO");
      loadParts(roleFilter);
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal memproses"); }
  };
  const removeCampaign = async (id) => { try { await api.delete(`/campaigns/${id}`); loadCampaigns(); } catch { toast.error("Gagal hapus"); } };

  return (
    <div data-testid="campaign-admin-panel">
      <Tabs defaultValue="manage">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <TabsList className="bg-showty-secondary">
            <TabsTrigger value="manage" data-testid="camp-tab-manage"><Megaphone className="w-4 h-4 mr-1.5" /> Kelola Campaign ({campaigns.length})</TabsTrigger>
            <TabsTrigger value="proofs" data-testid="camp-tab-proofs"><ImageIcon className="w-4 h-4 mr-1.5" /> Peserta & Tracking</TabsTrigger>
          </TabsList>
          <div className="flex gap-2">
            <Button onClick={exportExcel} variant="outline" className="rounded-full" data-testid="campaign-export-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel</Button>
            <CampaignDialog onDone={loadCampaigns} />
          </div>
        </div>

        <TabsContent value="manage" className="mt-2">
          {campaigns.length === 0 ? <Empty text="Belum ada campaign" /> : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="admin-campaigns">
              {campaigns.map((c) => (
                <div key={c.id} onClick={() => setPreview(c)} className="rounded-2xl border border-border bg-white overflow-hidden cursor-pointer hover:shadow-md transition-shadow" data-testid={`admin-campaign-${c.id}`}>
                  <div className="h-32 bg-showty-pink/30">{c.product_image && <img src={c.product_image} alt="" className="w-full h-full object-cover" />}</div>
                  <div className="p-4">
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-display text-lg text-showty-navy">{c.title}</h4>
                      <div className="flex gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
                        <CampaignDialog campaign={c} onDone={loadCampaigns} trigger={<button className="text-muted-foreground hover:text-showty-navy" data-testid={`campaign-edit-${c.id}`}><Pencil className="w-4 h-4" /></button>} />
                        <button onClick={() => removeCampaign(c.id)} className="text-muted-foreground hover:text-destructive" data-testid={`campaign-delete-${c.id}`}><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                    <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                      <span className="rounded-full bg-showty-navy text-white px-2 py-0.5">{c.category || "Campaign"}</span>
                      <span className="rounded-full bg-showty-red/10 text-showty-red px-2 py-0.5 flex items-center gap-1"><Link2 className="w-3 h-3" /> {PLATFORM_LABEL[c.platform || "tiktok"]}</span>
                      <span className="rounded-full bg-showty-cream px-2 py-0.5 text-showty-navy">{ROLE_LABEL[c.target_role]}</span>
                      {c.deadline && <span className="text-showty-red">Deadline {c.deadline}</span>}
                    </div>
                    <p className="mt-2 text-xs text-muted-foreground line-clamp-2">{c.product_knowledge}</p>
                    {c.form_link && <a onClick={(e) => e.stopPropagation()} href={c.form_link} target="_blank" rel="noreferrer" className="mt-2 inline-flex items-center gap-1 text-xs text-showty-red hover:underline" data-testid={`admin-form-link-${c.id}`}><Link2 className="w-3 h-3" /> Form: {c.form_label || "diisi user"}</a>}
                    {c.form_fields?.length > 0 && <div className="mt-2" onClick={(e) => e.stopPropagation()}><FormResponsesDialog campaign={c} /></div>}
                  </div>
                </div>
              ))}
            </div>
          )}
        </TabsContent>

        <TabsContent value="proofs" className="mt-2">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-5" data-testid="campaign-progress-summary">
            {[
              { id: "sum-total", label: "Total Peserta", value: counts.total, icon: Crown, tone: "text-showty-navy", hint: "Semua yang join campaign" },
              { id: "sum-proof", label: "Bukti Masuk", value: counts.withProof, icon: ImageIcon, tone: "text-showty-red", hint: "Screenshot bukti terupload di akun mereka" },
              { id: "sum-process", label: "Proses Review", value: counts.process, icon: BadgeCheck, tone: "text-showty-gold", hint: "Menunggu approval manager/CEO" },
              { id: "sum-done", label: "Selesai", value: counts.approved, icon: Crown, tone: "text-green-600", hint: "Final approved (CEO)" },
            ].map((c) => (
              <div key={c.id} className="rounded-2xl border border-border bg-white p-4" data-testid={c.id}>
                <c.icon className={`w-5 h-5 ${c.tone}`} />
                <p className="mt-2 font-display text-3xl text-showty-navy" data-testid={`${c.id}-value`}>{c.value}</p>
                <p className="text-xs font-medium text-showty-navy">{c.label}</p>
                <p className="text-[10px] text-muted-foreground mt-0.5">{c.hint}</p>
              </div>
            ))}
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <div className="inline-flex rounded-full border border-border bg-white p-1" data-testid="camp-role-filter">
              {[{ id: "beauty_creator", label: "Beauty Creators", icon: Crown }, { id: "affiliator", label: "Affiliators", icon: Video }].map((r) => (
                <button key={r.id} onClick={() => setRoleFilter(r.id)} data-testid={`camp-filter-${r.id}`}
                  className={`flex items-center gap-1.5 rounded-full px-4 py-2 text-sm ${roleFilter === r.id ? "bg-showty-navy text-white" : "text-muted-foreground hover:text-showty-navy"}`}>
                  <r.icon className="w-4 h-4" /> {r.label}
                </button>
              ))}
            </div>
            <select value={campFilter} onChange={(e) => setCampFilter(e.target.value)} className="h-10 rounded-full border border-border bg-white px-4 text-sm" data-testid="camp-campaign-filter">
              <option value="all">Semua campaign</option>
              {campaigns.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
            </select>
            <Button onClick={exportExcel} variant="outline" className="rounded-full ml-auto" data-testid="camp-export-btn"><FileSpreadsheet className="w-4 h-4 mr-1.5" /> Export Excel</Button>
          </div>
          <div className="flex flex-wrap gap-2 mb-4" data-testid="camp-status-filter">
            {STATUS_TABS.map((s) => {
              const n = s.id === "all" ? counts.total : counts[s.id];
              return (
                <button key={s.id} onClick={() => setStatusFilter(s.id)} data-testid={`camp-status-${s.id}`}
                  className={`rounded-full px-3.5 py-1.5 text-xs font-medium border ${statusFilter === s.id ? "bg-showty-red text-white border-showty-red" : "bg-white text-showty-navy border-border hover:bg-showty-pink/40"}`}>
                  {s.label} ({n})
                </button>
              );
            })}
          </div>
          {filtered.length === 0 ? <Empty text="Tidak ada peserta pada filter ini" /> : (
            <div className="space-y-3" data-testid="admin-participations">
              {filtered.map((p) => (
                <div key={p.id} className="rounded-xl border border-border bg-white p-4" data-testid={`admin-part-${p.id}`}>
                  <div className="flex flex-wrap items-center gap-4">
                    <div className="flex-1 min-w-[180px]">
                      <p className="font-medium text-showty-navy">{p.campaign_title}</p>
                      <p className="text-xs text-muted-foreground">{p.user_name} · {p.user_email}</p>
                      <div className="mt-1 flex flex-wrap items-center gap-2 text-xs">
                        {p.whatsapp ? (
                          <a href={`https://wa.me/${p.whatsapp.replace(/\D/g, "").replace(/^0/, "62")}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-green-700 hover:underline" data-testid={`part-wa-${p.id}`}>
                            <Phone className="w-3 h-3" /> {p.whatsapp}
                          </a>
                        ) : <span className="text-muted-foreground italic">No WA belum diisi</span>}
                        {p.city && <span className="inline-flex items-center gap-1 text-showty-navy"><MapPin className="w-3 h-3 text-showty-red" /> {p.city}</span>}
                      </div>
                      {p.verified_platforms?.length > 0 && (
                        <div className="mt-1 flex flex-wrap gap-1.5" data-testid={`admin-verified-${p.id}`}>
                          {p.verified_platforms.map((vp) => {
                            const val = vp === "tiktok" ? p.tiktok_url : p.shopee_url;
                            const isUrl = /^https?:\/\//i.test(val || "");
                            const cls = "text-[10px] rounded-full bg-green-100 text-green-700 px-2 py-0.5 font-semibold flex items-center gap-1";
                            return isUrl ? (
                              <a key={vp} href={val} target="_blank" rel="noreferrer" className={`${cls} hover:underline`} data-testid={`admin-verified-${p.id}-${vp}`}>
                                <BadgeCheck className="w-3 h-3" /> {PLATFORM_LABEL[vp]} terverifikasi
                              </a>
                            ) : (
                              <span key={vp} className={cls} data-testid={`admin-verified-${p.id}-${vp}`} title={val || ""}>
                                <BadgeCheck className="w-3 h-3" /> {PLATFORM_LABEL[vp]}{val ? ` (${val})` : " terverifikasi"}
                              </span>
                            );
                          })}
                        </div>
                      )}
                    </div>
                    <StatusBadge status={p.status === "joined" ? "pending" : p.status} />
                    <StageActions status={p.status} isSecretariat={isSecretariat} id={p.id} onAction={review} />
                    {p.status === "approved" && <TrackingDialog part={p} onDone={() => loadParts(roleFilter)} />}
                  </div>
                  {(p.status === "submitted" || p.status === "manager_approved" || p.status === "approved") && (
                    <div className="mt-3 flex flex-wrap items-start gap-3" data-testid={`proof-view-${p.id}`}>
                      {p.proof_photo && <ProofImage pid={p.id} />}
                      {p.proof_link && <a href={p.proof_link} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-showty-red hover:underline" data-testid={`proof-link-${p.id}`}><ExternalLink className="w-3 h-3" /> Buka link post</a>}
                    </div>
                  )}
                  {p.status === "joined" && <p className="mt-2 text-xs text-muted-foreground italic">Menunggu member upload bukti post.</p>}
                </div>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="campaign-preview-dialog">
          {preview && (
            <>
              <DialogHeader>
                <DialogTitle className="font-display text-2xl text-showty-navy">{preview.title}</DialogTitle>
                <DialogDescription>Preview campaign yang sudah kamu posting.</DialogDescription>
              </DialogHeader>
              {preview.product_image && <img src={preview.product_image} alt="" className="w-full h-44 object-cover rounded-xl" />}
              <div className="flex flex-wrap gap-2 text-xs">
                <span className="rounded-full bg-showty-navy text-white px-2 py-0.5">{preview.category || "Campaign"}</span>
                <span className="rounded-full bg-showty-red/10 text-showty-red px-2 py-0.5">{PLATFORM_LABEL[preview.platform || "tiktok"]}</span>
                <span className="rounded-full bg-showty-cream px-2 py-0.5 text-showty-navy">{ROLE_LABEL[preview.target_role]}</span>
                {preview.deadline && <span className="text-showty-red self-center">Deadline {preview.deadline}</span>}
              </div>
              <div className="rounded-xl bg-showty-cream border border-border p-4"><p className="text-[11px] uppercase tracking-wide text-showty-gold font-semibold">Product Knowledge</p><p className="text-sm text-showty-navy mt-1 whitespace-pre-wrap">{preview.product_knowledge || "—"}</p></div>
              <div className="rounded-xl border border-showty-red/20 bg-white p-4"><p className="text-[11px] uppercase tracking-wide text-showty-red font-semibold">SOW</p><p className="text-sm text-showty-navy mt-1 whitespace-pre-wrap">{preview.sow || "—"}</p></div>
              {preview.form_link && <a href={preview.form_link} target="_blank" rel="noreferrer" className="text-sm text-showty-red underline">Form eksternal untuk user</a>}
              {preview.form_fields?.length > 0 && <p className="text-xs text-muted-foreground">Form manual: {preview.form_fields.length} pertanyaan</p>}
              <div className="flex gap-2 pt-1">
                <CampaignDialog campaign={preview} onDone={() => { loadCampaigns(); setPreview(null); }} trigger={<Button variant="outline" className="rounded-full" data-testid="preview-edit-btn"><Pencil className="w-4 h-4 mr-1" /> Edit Campaign</Button>} />
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

const ProofImage = ({ pid }) => {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let active = true;
    api.get(`/campaign-participations/${pid}/proof-photo`, { responseType: "blob" })
      .then((r) => { if (active) setUrl(URL.createObjectURL(r.data)); }).catch(() => {});
    return () => { active = false; };
  }, [pid]);
  if (!url) return <div className="w-24 h-24 rounded-lg bg-showty-cream animate-pulse" />;
  return <a href={url} target="_blank" rel="noreferrer"><img src={url} alt="bukti" className="w-24 h-24 rounded-lg object-cover border border-border" data-testid={`proof-img-${pid}`} /></a>;
};

const StageActions = ({ status, isSecretariat, id, onAction }) => {
  if (status === "submitted") return (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => onAction(id, "manager_approved")} className="rounded-full bg-green-600 hover:bg-green-700 text-white" data-testid={`camp-approve-${id}`}>Approve (Manager)</Button>
      <Button size="sm" variant="outline" onClick={() => onAction(id, "rejected")} className="rounded-full border-destructive text-destructive hover:bg-destructive/10" data-testid={`camp-reject-${id}`}>Reject</Button>
    </div>
  );
  if (status === "manager_approved") return isSecretariat ? (
    <div className="flex gap-2">
      <Button size="sm" onClick={() => onAction(id, "approved")} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid={`camp-final-${id}`}>Final Approve (CEO)</Button>
      <Button size="sm" variant="outline" onClick={() => onAction(id, "rejected")} className="rounded-full border-destructive text-destructive hover:bg-destructive/10" data-testid={`camp-reject-${id}`}>Reject</Button>
    </div>
  ) : <span className="text-xs text-muted-foreground italic" data-testid={`camp-await-${id}`}>Menunggu final approval CEO</span>;
  return null;
};

const TrackingDialog = ({ part, onDone }) => {
  const [open, setOpen] = useState(false);
  const [f, setF] = useState({ courier: part.courier || "", tracking_number: part.tracking_number || "", tracking_status: part.tracking_status || "processing" });
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const save = async () => {
    try { await api.patch(`/campaign-participations/${part.id}/tracking`, f); toast.success("Tracking diperbarui"); setOpen(false); onDone?.(); }
    catch { toast.error("Gagal memperbarui tracking"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full border-showty-navy text-showty-navy hover:bg-showty-navy/10" data-testid={`camp-tracking-btn-${part.id}`}><Truck className="w-3.5 h-3.5 mr-1" /> {part.tracking_number ? "Edit Tracking" : "Set Tracking"}</Button></DialogTrigger>
      <DialogContent data-testid="camp-tracking-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">Tracking Pengiriman</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <p className="text-sm text-muted-foreground">{part.campaign_title} · {part.user_name}</p>
          <div><Label>Kurir</Label>
            <Select value={f.courier} onValueChange={(v) => set("courier", v)}>
              <SelectTrigger className="mt-1" data-testid="camp-tracking-courier"><SelectValue placeholder="Pilih kurir" /></SelectTrigger>
              <SelectContent><SelectItem value="JNE">JNE</SelectItem><SelectItem value="SPX">SPX (Shopee)</SelectItem><SelectItem value="JNT">JNT</SelectItem><SelectItem value="HALUU">HALUU</SelectItem></SelectContent>
            </Select>
          </div>
          <div><Label>No. Resi</Label><Input className="mt-1" value={f.tracking_number} onChange={(e) => set("tracking_number", e.target.value)} data-testid="camp-tracking-resi" /></div>
          <div><Label>Status</Label>
            <Select value={f.tracking_status} onValueChange={(v) => set("tracking_status", v)}>
              <SelectTrigger className="mt-1" data-testid="camp-tracking-status"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="processing">Diproses</SelectItem><SelectItem value="shipped">Dikirim</SelectItem><SelectItem value="delivered">Diterima</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="camp-tracking-save">Simpan Tracking</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const CampaignDialog = ({ onDone, campaign, trigger }) => {
  const [open, setOpen] = useState(false);
  const empty = { title: "", product_image: "", product_knowledge: "", sow: "", category: "Skincare", platform: "tiktok", form_link: "", form_label: "", form_fields: [], target_role: "both", deadline: "", active: true };
  const [f, setF] = useState(campaign ? { ...empty, ...campaign } : empty);
  const [uploading, setUploading] = useState(false);
  const set = (k, v) => setF((p) => ({ ...p, [k]: v }));
  const addField = () => set("form_fields", [...(f.form_fields || []), { id: `q${Date.now()}_${(f.form_fields || []).length}`, label: "", type: "text", required: false }]);
  const updateField = (i, k, v) => set("form_fields", (f.form_fields || []).map((x, idx) => (idx === i ? { ...x, [k]: v } : x)));
  const removeField = (i) => set("form_fields", (f.form_fields || []).filter((_, idx) => idx !== i));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setF(campaign ? { ...empty, ...campaign } : empty); }, [open]);
  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const r = await api.post("/media/upload", fd); set("product_image", r.data.url); toast.success("Foto terupload"); }
    catch { toast.error("Gagal upload foto"); }
    setUploading(false); e.target.value = "";
  };
  const save = async () => {
    if (!f.title.trim()) return toast.error("Judul campaign wajib diisi");
    const body = { title: f.title, product_image: f.product_image, product_knowledge: f.product_knowledge, sow: f.sow, category: f.category, platform: f.platform, form_link: f.form_link, form_label: f.form_label, form_fields: (f.form_fields || []).filter((x) => (x.label || "").trim()), target_role: f.target_role, deadline: f.deadline, active: f.active !== false };
    try {
      if (campaign) await api.patch(`/campaigns/${campaign.id}`, body);
      else { await api.post("/campaigns", body); setF(empty); }
      toast.success(campaign ? "Campaign diperbarui" : "Campaign dipublikasikan");
      setOpen(false); onDone?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Gagal menyimpan campaign"); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{trigger || <Button className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white" data-testid="campaign-add-btn"><Plus className="w-4 h-4 mr-1" /> Post Campaign</Button>}</DialogTrigger>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" data-testid="campaign-dialog">
        <DialogHeader><DialogTitle className="font-display text-2xl text-showty-navy">{campaign ? "Edit Campaign" : "Post Campaign Baru"}</DialogTitle></DialogHeader>
        <div className="space-y-3 py-1">
          <div><Label>Judul Campaign</Label><Input className="mt-1" value={f.title} onChange={(e) => set("title", e.target.value)} data-testid="campaign-title" /></div>
          <div>
            <Label>Upload Foto Produk</Label>
            <Input type="file" accept="image/*" className="mt-1" onChange={upload} data-testid="campaign-image-upload" />
            {uploading && <p className="text-xs text-showty-red mt-1">Mengupload…</p>}
            {f.product_image && <img src={f.product_image} alt="preview" className="mt-2 h-28 w-full object-cover rounded-lg border border-border" data-testid="campaign-image-preview" />}
          </div>
          <div><Label>Atau URL Foto (opsional)</Label><Input className="mt-1" placeholder="https://... / /images/..." value={f.product_image} onChange={(e) => set("product_image", e.target.value)} data-testid="campaign-image" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Kategori</Label>
              <Select value={f.category} onValueChange={(v) => set("category", v)}>
                <SelectTrigger className="mt-1" data-testid="campaign-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Skincare">Skincare</SelectItem>
                  <SelectItem value="Makeup">Makeup</SelectItem>
                  <SelectItem value="Bodycare">Bodycare</SelectItem>
                  <SelectItem value="Haircare">Haircare</SelectItem>
                  <SelectItem value="Event">Event</SelectItem>
                  <SelectItem value="Collaboration">Collaboration</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div><Label>Deadline Submission</Label><Input type="date" className="mt-1" value={(f.deadline || "").slice(0, 10)} onChange={(e) => set("deadline", e.target.value)} data-testid="campaign-deadline" /></div>
          </div>
          <div><Label>Product Knowledge</Label><Textarea rows={3} className="mt-1" placeholder="Kandungan, manfaat, keunggulan produk…" value={f.product_knowledge} onChange={(e) => set("product_knowledge", e.target.value)} data-testid="campaign-knowledge" /></div>
          <div><Label>SOW (Scope of Work)</Label><Textarea rows={4} className="mt-1" placeholder="Deliverable: jumlah konten, platform, hook, poin wajib, hashtag, tag akun, dll." value={f.sow} onChange={(e) => set("sow", e.target.value)} data-testid="campaign-sow" /></div>
          <div><Label>Link Form untuk User (Google Form/Typeform, opsional)</Label><Input className="mt-1" placeholder="https://forms.gle/..." value={f.form_link} onChange={(e) => set("form_link", e.target.value)} data-testid="campaign-form-link" /></div>
          <div><Label>Label Tombol Form (opsional)</Label><Input className="mt-1" placeholder="cth: Isi Form Data Diri / Alamat" value={f.form_label} onChange={(e) => set("form_label", e.target.value)} data-testid="campaign-form-label" /></div>
          <div className="rounded-xl border border-border bg-showty-cream/40 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-showty-navy">Form Manual (diisi user di dalam app)</Label>
              <Button type="button" size="sm" variant="outline" className="rounded-full h-7 text-xs" onClick={addField} data-testid="form-add-field"><Plus className="w-3 h-3 mr-1" />Pertanyaan</Button>
            </div>
            {(f.form_fields || []).length === 0 && <p className="text-[11px] text-muted-foreground">Belum ada pertanyaan. Klik "Pertanyaan" untuk membuat field yang harus diisi user (mis. ukuran baju, catatan alamat, dsb.).</p>}
            {(f.form_fields || []).map((fld, i) => (
              <div key={fld.id} className="flex items-center gap-2" data-testid={`form-field-${i}`}>
                <Input className="flex-1 h-8 text-sm" placeholder="Pertanyaan…" value={fld.label} onChange={(e) => updateField(i, "label", e.target.value)} data-testid={`form-field-label-${i}`} />
                <select className="h-8 rounded-md border border-border text-xs px-1 bg-white" value={fld.type} onChange={(e) => updateField(i, "type", e.target.value)} data-testid={`form-field-type-${i}`}>
                  <option value="text">Teks</option>
                  <option value="textarea">Paragraf</option>
                  <option value="number">Angka</option>
                </select>
                <label className="text-[11px] flex items-center gap-1 shrink-0"><input type="checkbox" checked={!!fld.required} onChange={(e) => updateField(i, "required", e.target.checked)} data-testid={`form-field-required-${i}`} /> wajib</label>
                <button type="button" onClick={() => removeField(i)} className="text-muted-foreground hover:text-destructive shrink-0" data-testid={`form-field-remove-${i}`}><Trash2 className="w-4 h-4" /></button>
              </div>
            ))}
          </div>
          <div>
            <Label>Target Peran</Label>
            <Select value={f.target_role} onValueChange={(v) => set("target_role", v)}>
              <SelectTrigger className="mt-1" data-testid="campaign-target"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="both">Semua</SelectItem><SelectItem value="beauty_creator">Beauty Creator</SelectItem><SelectItem value="affiliator">Affiliator</SelectItem></SelectContent>
            </Select>
          </div>
          <div>
            <Label>Platform Verifikasi (akun yang dihubungkan saat join)</Label>
            <Select value={f.platform} onValueChange={(v) => set("platform", v)}>
              <SelectTrigger className="mt-1" data-testid="campaign-platform"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="tiktok">TikTok</SelectItem>
                <SelectItem value="shopee">Shopee</SelectItem>
                <SelectItem value="both">TikTok + Shopee</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        <DialogFooter><Button onClick={save} disabled={uploading} className="rounded-full bg-showty-red hover:bg-[#9e1f24] text-white w-full" data-testid="campaign-save">{campaign ? "Simpan Perubahan" : "Publikasikan"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

const FormResponsesDialog = ({ campaign }) => {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  useEffect(() => { if (open) api.get(`/campaigns/${campaign.id}/form-responses`).then((r) => setItems(r.data)).catch(() => {}); }, [open, campaign.id]);
  const fields = campaign.form_fields || [];
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full h-7 text-xs" data-testid={`form-responses-${campaign.id}`}><FileText className="w-3 h-3 mr-1" /> Jawaban Form</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="form-responses-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-2xl text-showty-navy">Jawaban Form — {campaign.title}</DialogTitle>
          <DialogDescription>Semua jawaban form manual yang dikirim member untuk campaign ini.</DialogDescription>
        </DialogHeader>
        {items.length === 0 ? <p className="py-8 text-center text-sm text-muted-foreground" data-testid="form-responses-empty">Belum ada yang mengisi form.</p> : (
          <div className="space-y-3">
            {items.map((it) => (
              <div key={it.id} className="rounded-xl border border-border p-3" data-testid={`form-response-${it.id}`}>
                <p className="font-medium text-showty-navy text-sm">{it.user_name} <span className="text-muted-foreground font-normal">· {it.user_email}</span></p>
                <div className="mt-1.5 space-y-1">
                  {fields.map((fld) => <p key={fld.id} className="text-xs text-showty-navy"><span className="text-muted-foreground">{fld.label}:</span> {String(it.answers?.[fld.id] ?? "—") || "—"}</p>)}
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

const Empty = ({ text }) => (
  <div className="rounded-2xl border border-dashed border-border bg-white/50 py-14 text-center text-sm text-muted-foreground" data-testid="empty-state">{text}</div>
);
